import React from "react";
import { cx } from "./cx";

// Table — token-driven data table.
// columns: [{ key, header, render?, align?, width?, mono? }]
//   render?(row, index) -> node; align? "left"|"center"|"right" (default left);
//   width? css width; mono? render this column's cells in JetBrains Mono
//   (use for numeric / ID / price / time columns, per brand).
// rows: array of row objects. onRowClick?(row, index). loading: bool. empty: node.
//
// REFINEMENT NOTES:
// - Header tracking tightened from tracking-brand (0.14em, an eyebrow value) to
//   tracking-wider (0.05em) — crisper at 11px, still clearly a label.
// - Body rows tightened to py-2.5 (was py-3) to raise density; header keeps py-3.
// - Row hover is now navy-tinted (hover:bg-primary/5) — the old bg-surface was
//   nearly invisible against white.
// - mono columns get tabular-nums so fares / times / IDs align by digit.
// - Loading skeleton mirrors column shape: short right-aligned bars for mono
//   columns, wide bars for text columns.
export function Table({
  columns = [],
  rows = [],
  onRowClick,
  loading = false,
  empty = "No records.",
  className = "",
}) {
  const alignClass = (a) =>
    a === "right" ? "text-right" : a === "center" ? "text-center" : "text-left";

  return (
    <div className={cx("overflow-auto rounded-card border border-border bg-white", className)}>
      <table className="w-full border-collapse text-sm">
        <thead className="sticky top-0 z-10 bg-surface">
          <tr>
            {columns.map((col) => (
              <th
                key={col.key}
                style={{ width: col.width }}
                className={cx(
                  "px-4 py-3 text-11 font-semibold uppercase tracking-wider text-body/55 border-b border-border whitespace-nowrap",
                  alignClass(col.align)
                )}
              >
                {col.header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {loading ? (
            Array.from({ length: 4 }).map((_, r) => (
              <tr key={r} className="border-b border-border last:border-0">
                {columns.map((col) => (
                  <td key={col.key} className={cx("px-4 py-2.5", alignClass(col.align))}>
                    <div
                      className={cx(
                        "h-2.5 rounded bg-border animate-pulse",
                        col.mono ? "w-12" : "w-2/3",
                        col.align === "right" && "ml-auto",
                        col.align === "center" && "mx-auto"
                      )}
                    />
                  </td>
                ))}
              </tr>
            ))
          ) : rows.length === 0 ? (
            <tr>
              <td
                colSpan={columns.length || 1}
                className="px-4 py-14 text-center text-sm text-body/50"
              >
                {empty}
              </td>
            </tr>
          ) : (
            rows.map((row, i) => (
              <tr
                key={row.id ?? i}
                onClick={onRowClick ? () => onRowClick(row, i) : undefined}
                className={cx(
                  "border-b border-border last:border-0 transition-colors",
                  onRowClick && "cursor-pointer hover:bg-primary/5"
                )}
              >
                {columns.map((col) => (
                  <td
                    key={col.key}
                    className={cx(
                      "px-4 py-2.5 text-body align-middle",
                      col.mono && "font-mono tabular-nums",
                      alignClass(col.align)
                    )}
                  >
                    {col.render ? col.render(row, i) : row[col.key]}
                  </td>
                ))}
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
}
